import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Icon from "../Utils/Icon";
import FormatDate from "../Utils/FormatDate";
import Amount from "../Utils/Amount";
import Loader from "../Utils/Loader";

import classes from './styles.module.css';

function formatBalance(value) {
    if (value === null || value === undefined) {
        return '0';
    }
    return Number(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function BalanceSection({section, sectionTitle}) {
    if (!section) return null;
    var labels = section.labels || {};
    var hints = section.hints || {};
    const intl = useIntl();
    var transactionLabel = labels.transactionBalance || intl.formatMessage({ id: 'BalanceWidget.transactionBalance', defaultMessage: 'Транзакционный баланс' });
    var settlementLabel = labels.settlementBalance || intl.formatMessage({ id: 'BalanceWidget.settlementLabel.reimbursement', defaultMessage: 'Возмещение' });
    var actualLabel = labels.actualBalance || intl.formatMessage({ id: 'BalanceWidget.actualBalance', defaultMessage: 'Действительный баланс' });

    return (
        <div className={classes.ExtendedBalances}>
            {sectionTitle && (
                <div className={classes.SectionTitle} style={{fontWeight: 600, fontSize: 13, color: '#555', padding: '4px 0'}}>
                    {sectionTitle}
                </div>
            )}
            <div className={classes.BalanceRow + ' ' + classes.TransactionBalance} title={hints.transactionBalance || ''}>
                <span className={classes.BalanceLabel}>{transactionLabel}</span>
                <span className={classes.BalanceValue}>
                    {formatBalance(section.transactionBalanceFloat)} &#8381;
                </span>
            </div>
            <div className={classes.BalanceRow + ' ' + classes.SettlementBalance} title={hints.settlementBalance || ''}>
                <span className={classes.BalanceLabel}>{settlementLabel}</span>
                <span className={classes.BalanceValue}>
                    {formatBalance(section.settlementBalanceFloat)} &#8381;
                </span>
            </div>
            <div className={classes.BalanceRow + ' ' + classes.ActualBalance} title={hints.actualBalance || ''}>
                <span className={classes.BalanceLabel}>{actualLabel}</span>
                <span className={classes.BalanceValue}>
                    {formatBalance(section.actualBalanceFloat)} &#8381;
                </span>
            </div>
        </div>
    );
}

function ExtendedBalancesView({totals}) {
    const intl = useIntl();
    // Новый формат: разделённые payin / payout секции. Для mixed-мерчантов — обе.
    var hasPayin = totals.payin && totals.payin.count > 0;
    var hasPayout = totals.payout && totals.payout.count > 0;
    var isMixed = totals.hasMixed || (hasPayin && hasPayout);

    if (isMixed) {
        return (
            <React.Fragment>
                <BalanceSection section={totals.payin} sectionTitle={intl.formatMessage({ id: 'BalanceWidget.section.payin', defaultMessage: 'Приём платежей' })}/>
                <div style={{height: 8}}/>
                <BalanceSection section={totals.payout} sectionTitle={intl.formatMessage({ id: 'BalanceWidget.section.payout', defaultMessage: 'Выплаты' })}/>
            </React.Fragment>
        );
    }

    if (hasPayout) {
        return <BalanceSection section={totals.payout}/>;
    }
    if (hasPayin) {
        return <BalanceSection section={totals.payin}/>;
    }

    // Fallback для старого формата ответа (без payin/payout секций)
    var legacyLabels = totals.labels || {};
    var legacyHints = totals.hints || {};
    var settlementLabel = legacyLabels.settlementBalance || intl.formatMessage({
        id: totals.hasCrm ? 'BalanceWidget.settlementLabel.reserve' : 'BalanceWidget.settlementLabel.reimbursement',
        defaultMessage: totals.hasCrm ? 'Reserve' : 'Reimbursement',
    });
    var transactionLabel = legacyLabels.transactionBalance || intl.formatMessage({ id: 'BalanceWidget.transactionBalance', defaultMessage: 'Транзакционный баланс' });
    var actualLabel = legacyLabels.actualBalance || intl.formatMessage({ id: 'BalanceWidget.actualBalance', defaultMessage: 'Действительный баланс' });

    return (
        <div className={classes.ExtendedBalances}>
            <div className={classes.BalanceRow + ' ' + classes.TransactionBalance} title={legacyHints.transactionBalance || ''}>
                <span className={classes.BalanceLabel}>
                    <FormattedMessage id="BalanceWidget.transactionBalance" defaultMessage={transactionLabel}/>
                </span>
                <span className={classes.BalanceValue}>
                    {formatBalance(totals.transactionBalanceFloat)} &#8381;
                </span>
            </div>
            <div className={classes.BalanceRow + ' ' + classes.SettlementBalance} title={legacyHints.settlementBalance || ''}>
                <span className={classes.BalanceLabel}>
                    {settlementLabel}
                </span>
                <span className={classes.BalanceValue}>
                    {formatBalance(totals.settlementBalanceFloat)} &#8381;
                </span>
            </div>
            <div className={classes.BalanceRow + ' ' + classes.ActualBalance} title={legacyHints.actualBalance || ''}>
                <span className={classes.BalanceLabel}>
                    <FormattedMessage id="BalanceWidget.actualBalance" defaultMessage={actualLabel}/>
                </span>
                <span className={classes.BalanceValue}>
                    {formatBalance(totals.actualBalanceFloat)} &#8381;
                </span>
            </div>
        </div>
    );
}

function BalanceWidget({data, extendedBalances, selectedProject, refillsLinkInHeader, isLoading}) {
    const [count] = useState(data ? data.count : 0);

    var hasExtended = extendedBalances && extendedBalances.totals;

    let fontSize = '1.4375rem';
    switch (count) {
        case 1:
            fontSize = '1.4375rem';
            break;
        case 2:
            fontSize = '1.3375rem';
            break;
        case 3:
            fontSize = '1.2375rem';
            break;
        default:
            fontSize = '1.4375rem';
    }

    return (
        <div className={classes.Card}>
            <div className={classes.CardHeader}>
                <h6>
                    <FormattedMessage id="Dashboard.title.balance" defaultMessage="Баланс"/>
                </h6>

                {refillsLinkInHeader && (
                    <Link className={classes.HeaderLink} to={'/refills'}>
                        <Icon className={classes.HeaderIcon} icon="history" size={'20px'}/>
                        <span>
                            <FormattedMessage id="Dashboard.title.refills" defaultMessage="Пополнения"/>
            </span>
                    </Link>
                )}
            </div>

            {isLoading ? <Loader/> : (
                <div className={classes.CardBody}>
                    {hasExtended ? (
                        <React.Fragment>
                            <div className={classes.ExtendedMediaIcon}>
                                <div className={classes.IconCircle + ' ' + classes.IconCircleGreen}>
                                    <Icon color={'#35C98E'} icon="wallet" size={'20px'}/>
                                </div>
                            </div>

                            <div className={classes.MediaBody}>
                                <ExtendedBalancesView totals={extendedBalances.totals}/>

                                <div className={classes.MediaMore}>
                                    <div className={classes.Desc}>
                                        <FormattedMessage
                                            id="Dashboard.title.balanceOnDate"
                                            defaultMessage="Баланс на {date}"
                                            values={{
                                                date: <FormatDate format={{day: 'numeric', month: 'numeric', year: 'numeric'}}/>
                                            }}
                                        />
                                    </div>

                                    {!refillsLinkInHeader && (
                                        <div style={{display: "none"}}>
                                            <Link className={classes.Link} to={'/refills'}>
                                                <Icon className={classes.Icon} icon="history" size={'20px'}/>
                                                <span>
                                                    <FormattedMessage id="Dashboard.title.refills" defaultMessage="Пополнения"/>
                                                </span>
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div className={classes.MediaIcon}>
                                <Icon color={'#35C98E'} icon="wallet" size={'20px'}/>
                            </div>

                            <div className={classes.MediaBody}>
                                <div className={classes.Balances}>
                                    {selectedProject ? (
                                        selectedProject.balance.map(function(el, index) {
                                            const currency = Object.keys(el)[0];
                                            return (
                                                <h2 style={{fontSize: fontSize}} key={index}>
                                                    <Amount amount={el[currency]} currency={currency}/>
                                                </h2>
                                            );
                                        })
                                    ) : (
                                        data && data.map ? data.map(function(el, index) {
                                            const currency = Object.keys(el)[0];
                                            return (
                                                <h2 style={{fontSize: fontSize}} key={index}>
                                                    <Amount amount={el[currency]} currency={currency}/>
                                                </h2>
                                            );
                                        }) : null
                                    )}
                                </div>

                                <div className={classes.MediaMore}>
                                    <div className={classes.Desc}>
                                        <FormattedMessage
                                            id="Dashboard.title.balanceOnDate"
                                            defaultMessage="Баланс на {date}"
                                            values={{
                                                date: <FormatDate format={{day: 'numeric', month: 'numeric', year: 'numeric'}}/>
                                            }}
                                        />
                                    </div>

                                    {!refillsLinkInHeader && (
                                        <div style={{display: "none"}}>
                                            <Link className={classes.Link} to={'/refills'}>
                                                <Icon className={classes.Icon} icon="history" size={'20px'}/>
                                                <span>
                                                    <FormattedMessage id="Dashboard.title.refills" defaultMessage="Пополнения"/>
                                                </span>
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            )}
        </div>
    );
}

export default BalanceWidget;



// WEBPACK FOOTER //
// ./src/Components/BalanceWidget/index.js